import React, { useEffect, useRef, useState } from 'react';
import API from '../../utils/ApiConfig';
import { AUTOMATED_ONBOARDING_INFO } from '../../utils/ApiEndpoints';
import {
  AUTOONBOARD_ACCOUNT_GROUP_MAPPING_PAGE,
  AUTOONBOARD_CREATE_BUDGET_PAGE,
  AUTOONBOARD_FISCAL_CALENDAR_PAGE,
  AUTOONBOARD_REVIEW_DATA_PAGE,
} from '../../utils/AutoOnboardConfig';
import AutomatedOnboardingHeader from '../AutomatedOnboardingHeader/AutomatedOnboardingHeader';
import AutomatedOnboardingTime from '../AutomatedOnboardingTime/AutomatedOnboardingTime';
import AutomatedOnboardingAccountGroupMapping from '../AutomatedOnboardingAccountGroupMapping/AutomatedOnboardingAccountGroupMapping';
import HorizontalStepperPM from '../common/HorizontalStepperPM/HorizontalStepperPM';
import { useTranslation } from 'react-i18next';
import AutomatedOnboardingReviewData from '../AutomatedOnboardingReviewData/AutomatedOnboardingReviewData';
import AutomatedOnboardingCreateBudget from '../AutomatedOnboardingCreateBudget/AutomatedOnboardingCreateBudget';
import SuccessMessage from '../common/SuccessMessage/SuccessMessage';
import ErrorMessage from '../common/ErrorMessage/ErrorMessage';

const AutomatedOnboarding = props => {
  const [activeStep, setActiveStep] = useState(-1);
  const [errorMessage, setErrorMessage] = useState({});
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const [budgetVersion, setBudgetVersion] = useState(null);
  const [numOfYears, setNumOfYears] = useState(0);

  const {
    services: { CommonService },
  } = props;

  const accountId = CommonService.auth.getCurrentUserAccountID();

  const { t } = useTranslation();

  const tc = (e, data) => (data ? t(`autoonboard_common:${e}`, data) : t(`autoonboard_common:${e}`));

  const isAutoOnboardingWithoutSampleBudgetEnabled = CommonService.isAutoOnboardingWithoutSampleBudgetEnabled;
  const createBudgetOrPlanStep = isAutoOnboardingWithoutSampleBudgetEnabled ? tc('CREATE_PLAN_STEP_LABEL') : tc('CREATE_BUDGET_STEP_LABEL');

  useEffect(() => {
    API.get(AUTOMATED_ONBOARDING_INFO(accountId))
      .then(onboardingInfo => {
        let { data } = onboardingInfo;
        setBudgetVersion(data.budgetVersion);
        setNumOfYears(data.numOfYears);
        if (!data.currentPage || data.currentPage === AUTOONBOARD_FISCAL_CALENDAR_PAGE) {
          setActiveStep(0);
        } else if (data.currentPage === AUTOONBOARD_ACCOUNT_GROUP_MAPPING_PAGE) {
          setActiveStep(1);
        } else if (data.currentPage === AUTOONBOARD_REVIEW_DATA_PAGE) {
          setActiveStep(2);
        } else if (data.currentPage === AUTOONBOARD_CREATE_BUDGET_PAGE) {
          setActiveStep(3);
        } else {
          handlePageNavigationError(null);
          setActiveStep(-1);
        }
      })
      .catch(err => {
        handleCustomError(err); //...no custom message will trigger the 'unknown error' message
        setActiveStep(-1);
      });
  }, []);

  useEffect(() => {
    setTimeout(() => {
      $('.p-menuitem-link.p-step-passed').parent().css('opacity', 1);
    }, 0);
  }, [activeStep]);

  const stepTemplate = (item, options) => {
    const index = _.findIndex(options.props.model, { label: item.label });
    const stepPassed = index < options.props.activeIndex;

    return (
      <span className={`${options.className} ${stepPassed ? 'p-step-passed' : ''}`}>
        <span className={`${options.numberClassName} ${stepPassed ? 'material-icons' : ''}`}>{stepPassed ? 'check_circle' : index + 1}</span>
        <span className={options.labelClassName}>{item.label}</span>
      </span>
    );
  };

  const toast = useRef(null);
  const stepItems = [
    {
      label: tc('FISCAL_CALENDAR_STEP_LABEL'),
      template: stepTemplate,
      command: event => {
        toast.current.show({ severity: 'success', summary: event.item.label });
      },
    },
    {
      label: tc('ACCOUNT_GRP_MAP_STEP_LABEL'),
      template: stepTemplate,
      command: event => {
        toast.current.show({ severity: 'success', summary: event.item.label });
      },
    },
    {
      label: tc('REVIEW_DATA_STEP_LABEL'),
      template: stepTemplate,
      command: event => {
        toast.current.show({ severity: 'success', summary: event.item.label });
      },
    },
    {
      label: createBudgetOrPlanStep,
      template: stepTemplate,
      command: event => {
        toast.current.show({ severity: 'success', summary: event.item.label });
      },
    },
  ];

  //...error handling methods does not utilize the errorObject as backend doesn't use any error codes yet
  //...once/if backend starts using different error codes, errorObject may be used to retrieve appropriate error details message
  const handlePageInitializationError = errorObject => {
    let errorMessage = {
      key: 'AUTOMATED_ONBOARDING_PAGEINIT_ERROR',
    };
    setErrorMessage(errorMessage);
    setShowErrorMessage(true);
  };

  const handleDataSaveError = errorObject => {
    let errorMessage = {
      key: 'AUTOMATED_ONBOARDING_DATASAVE_ERROR',
    };
    setErrorMessage(errorMessage);
    setShowErrorMessage(true);
  };

  const handlePageNavigationError = errorObject => {
    let errorMessage = {
      key: 'AUTOMATED_ONBOARDING_NAVIGATION_ERROR',
    };
    setErrorMessage(errorMessage);
    setShowErrorMessage(true);
  };

  const handleCustomError = (errorObject, customErrorMessage) => {
    let errorMessage = null;
    if (!!customErrorMessage) {
      errorMessage = {
        message: customErrorMessage,
      };
    } else {
      errorMessage = {
        key: 'AUTOMATED_ONBOARDING_UNEXPECTED_ERROR',
      };
    }
    setErrorMessage(errorMessage);
    setShowErrorMessage(true);
  };

  const closeErrorMessage = () => {
    setShowErrorMessage(false);
  };

  const handleDataSaveNotification = () => {
    setSuccessMessage(tc('AUTOMATED_ONBOARDING_SAVE_SUCCESSFUL'));
    setShowSuccessMessage(true);
  };

  const handleCustomNotification = message => {
    setSuccessMessage(message);
    setShowSuccessMessage(true);
  };

  return (
    <div className="automated-onboard">
      <AutomatedOnboardingHeader commonProps={props} budgetVersion={budgetVersion} numOfYears={numOfYears} />
      <div className="automated-onboarding-body">
        <div className="automated-onboarding-steps">
          <HorizontalStepperPM activeStep={activeStep} stepItems={stepItems} toast={toast} />
        </div>
        <div className="automated-onboarding-content">
          {activeStep === 0 && (
            <AutomatedOnboardingTime
              commonProps={props}
              activeStep={activeStep}
              onActiveStepChange={s => setActiveStep(s)}
              onInitializationError={e => handlePageInitializationError(e)}
              onDataSaveError={e => handleDataSaveError(e)}
              onNavigationError={e => handlePageNavigationError(e)}
              onCustomError={(e, customErrorMessage) => handleCustomError(e, customErrorMessage)}
              onDataSaveNotification={() => handleDataSaveNotification()}
              onCustomNotification={message => handleCustomNotification(message)}
            />
          )}
          {activeStep === 1 && (
            <AutomatedOnboardingAccountGroupMapping
              commonProps={props}
              activeStep={activeStep}
              onActiveStepChange={s => setActiveStep(s)}
              onInitializationError={e => handlePageInitializationError(e)}
              onDataSaveError={e => handleDataSaveError(e)}
              onNavigationError={e => handlePageNavigationError(e)}
              onCustomError={(e, customErrorMessage) => handleCustomError(e, customErrorMessage)}
              onDataSaveNotification={() => handleDataSaveNotification()}
              onCustomNotification={message => handleCustomNotification(message)}
            />
          )}
          {activeStep === 2 && (
            <AutomatedOnboardingReviewData
              commonProps={props}
              versionName={budgetVersion}
              activeStep={activeStep}
              onActiveStepChange={s => setActiveStep(s)}
              onInitializationError={e => handlePageInitializationError(e)}
              onDataSaveError={e => handleDataSaveError(e)}
              onNavigationError={e => handlePageNavigationError(e)}
              onCustomError={(e, customErrorMessage) => handleCustomError(e, customErrorMessage)}
              onDataSaveNotification={() => handleDataSaveNotification()}
              onCustomNotification={message => handleCustomNotification(message)}
            />
          )}
          {activeStep === 3 && (
            <AutomatedOnboardingCreateBudget
              commonProps={props}
              versionName={budgetVersion}
              activeStep={activeStep}
              onActiveStepChange={s => setActiveStep(s)}
              onInitializationError={e => handlePageInitializationError(e)}
              onDataSaveError={e => handleDataSaveError(e)}
              onNavigationError={e => handlePageNavigationError(e)}
              onCustomError={(e, customErrorMessage) => handleCustomError(e, customErrorMessage)}
              onDataSaveNotification={() => handleDataSaveNotification()}
              onCustomNotification={message => handleCustomNotification(message)}
            />
          )}
        </div>
      </div>

      <SuccessMessage open={showSuccessMessage} setOpen={setShowSuccessMessage} message={successMessage} type="success" />

      <ErrorMessage
        t={!!errorMessage.key && tc}
        i18nKey={errorMessage.key}
        open={showErrorMessage}
        handleClose={closeErrorMessage}
        message={errorMessage.message}></ErrorMessage>
    </div>
  );
};

export default AutomatedOnboarding;